import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { COMPANY as C } from "@/lib/legal";

const inp = "w-full h-11 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70";

export default function WithdrawPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", order_number: params.get("ordine") || "", items: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    if (user) setF((p) => ({ ...p, name: p.name || user.name || "", email: p.email || user.email }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/withdrawals", f);
      setDone(data);
    } catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };

  if (user === null) return <div className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user)
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 space-y-5" data-testid="withdraw-login-required">
        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase text-brand-navy">Recedere dal contratto</h1>
        <p className="text-slate-700">Per proteggere i tuoi ordini, la dichiarazione di recesso si invia dal tuo account: accedi con l'email usata per l'ordine e compila il modulo.</p>
        <p className="text-sm text-slate-600">Non riesci ad accedere? Scrivi a <a className="underline font-semibold" href={`mailto:${C.email}`}>{C.email}</a> indicando il numero d'ordine.</p>
        <Link data-testid="withdraw-login-btn" to={`/accedi?next=${encodeURIComponent(`/recesso/modulo${params.get("ordine") ? `?ordine=${params.get("ordine")}` : ""}`)}`} className="press inline-flex h-12 px-6 items-center rounded-md bg-brand-navy text-white font-semibold">Accedi per continuare</Link>
      </div>
    );
  if (done)
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 space-y-5" data-testid="withdraw-success">
        <CheckCircle2 className="w-12 h-12 text-green-600" />
        <h1 className="text-4xl font-extrabold uppercase text-brand-navy">Recesso inviato</h1>
        <p>Abbiamo ricevuto la tua dichiarazione di recesso per l'ordine <b>{done.order_number}</b> il <b data-testid="withdraw-received-at">{new Date(done.received_at).toLocaleString("it-IT")}</b>.</p>
        <p>Ti abbiamo inviato un avviso di ricevimento a <b>{user.email}</b> con il contenuto della dichiarazione. Ti contatteremo per le istruzioni di reso e il rimborso.</p>
        <Link to="/recesso" className="underline font-semibold text-brand-navy">Leggi l'informativa su recesso e rimborsi</Link>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto px-4 py-14 space-y-6">
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase text-brand-navy">Recedere dal contratto</h1>
        <p className="mt-2 text-sm text-slate-600">Dichiarazione di recesso online (art. 54-bis Codice del Consumo). Compila o conferma i dati e premi "Conferma recesso".</p>
      </div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        I timbri personalizzati sono esclusi dal recesso (art. 59, lett. c, Codice del Consumo); il recesso vale per gli accessori non personalizzati, come i tamponi di ricambio. Per prodotti difettosi o non conformi scrivi a {C.email}.
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="withdraw-form">
        <label className="block text-sm font-semibold">Nome e cognome *<input data-testid="withdraw-name-input" required minLength={2} className={inp} value={f.name} onChange={set("name")} /></label>
        <label className="block text-sm font-semibold">Email del tuo account (riceverai qui la conferma)<input data-testid="withdraw-email-input" readOnly type="email" className={inp + " bg-slate-50"} value={user.email} /></label>
        <label className="block text-sm font-semibold">Numero d'ordine *<input data-testid="withdraw-order-input" required placeholder="HS-2026-00001" className={inp + " font-mono uppercase"} value={f.order_number} onChange={set("order_number")} /></label>
        <label className="block text-sm font-semibold">Beni per cui recedi <span className="font-normal text-slate-500">(vuoto = tutto l'ordine)</span><input data-testid="withdraw-items-input" className={inp} value={f.items} onChange={set("items")} placeholder="es. 2 tamponi di ricambio blu" /></label>
        <label className="block text-sm font-semibold">Note <span className="font-normal text-slate-500">(facoltativo, non serve indicare il motivo)</span><textarea data-testid="withdraw-notes-input" rows={3} className={inp + " h-auto py-2"} value={f.notes} onChange={set("notes")} /></label>
        {err && <p className="text-sm text-red-600" data-testid="withdraw-error">{err}</p>}
        <button data-testid="withdraw-confirm-btn" disabled={busy} className="press w-full h-12 rounded-md bg-brand-navy text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Conferma recesso
        </button>
      </form>
    </div>
  );
}
